import React, { useState, useEffect, useCallback } from 'react';
import './Collection.css';
import NFTCard from '../components/NFTCard';
import WalletSearchBox from '../components/WalletSearchBox';
import useWalletFilter from '../hooks/useWalletFilter';

const PixelCollection = () => {
  const wallet = useWalletFilter();
  const [nftData, setNftData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [rarityMethod, setRarityMethod] = useState('score');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [showOnlyExisting, setShowOnlyExisting] = useState(true);

  const loadNFTData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('/pixel_ranks.json');
      const data = await response.json();

      // Sort data based on selected rarity method
      let sortedData;
      switch (rarityMethod) {
        case 'average':
          sortedData = [...data].sort(
            (a, b) => a.rankings.average_trait_rarity.rank - b.rankings.average_trait_rarity.rank
          );
          break;
        case 'score':
          sortedData = [...data].sort(
            (a, b) => a.rankings.rarity_score.rank - b.rankings.rarity_score.rank
          );
          break;
        case 'statistical':
          sortedData = [...data].sort(
            (a, b) => a.rankings.statistical_rarity.rank - b.rankings.statistical_rarity.rank
          );
          break;
        case 'jaccard':
          sortedData = [...data].sort(
            (a, b) => a.rankings.jaccard_distance.rank - b.rankings.jaccard_distance.rank
          );
          break;
        case 'traitRating':
          sortedData = [...data].sort(
            (a, b) => a.rankings.trait_rarity_rating.rank - b.rankings.trait_rarity_rating.rank
          );
          break;
        default:
          sortedData = [...data].sort(
            (a, b) => a.rankings.rarity_score.rank - b.rankings.rarity_score.rank
          );
      }

      // Add token ID extraction for filtering
      const dataWithTokenIds = sortedData.map((nft) => {
        // Extract token ID from name (assuming format "Pixel Ape #XXXX" or similar)
        const match = nft.name.match(/#(\d+)/);
        const tokenId = match ? parseInt(match[1]) : 0;

        return {
          ...nft,
          tokenId,
        };
      });

      setNftData(dataWithTokenIds);
      setFilteredData(dataWithTokenIds);
    } catch (error) {
      console.error('Error loading NFT data:', error);
    } finally {
      setLoading(false);
    }
  }, [rarityMethod]);

  useEffect(() => {
    loadNFTData();
  }, [loadNFTData]);

  useEffect(() => {
    let filtered = nftData;

    // Wallet-address filter takes precedence over substring search.
    if (wallet.isWalletSearch) {
      filtered = filtered.filter((nft) =>
        wallet.ownedIds.has((nft.NFTokenID || '').toUpperCase())
      );
    } else if (searchTerm) {
      filtered = filtered.filter(
        (nft) =>
          nft.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          nft.tokenId.toString().includes(searchTerm) ||
          nft.attributes.some((attr) => attr.value.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filter by existing NFTs if toggle is on (based on NFTokenID presence)
    if (showOnlyExisting) {
      filtered = filtered.filter((nft) => nft.NFTokenID && nft.NFTokenID.trim() !== '');
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [searchTerm, nftData, showOnlyExisting, wallet.isWalletSearch, wallet.ownedIds]);

  const handleRarityMethodChange = (method) => {
    setRarityMethod(method);
    setCurrentPage(1);
  };

  const handleShowOnlyExistingChange = (checked) => {
    setShowOnlyExisting(checked);
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredData.slice(startIndex, startIndex + itemsPerPage);

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className='collection-page'>
      <div className='collection-header'>
        <h1>Money Minded Pixel Apes</h1>
        <p>Explore the rarity rankings of our pixel art collection</p>
      </div>

      <div className='collection-controls'>
        <div className='controls-row'>
          <div className='rarity-selector'>
            <label>Rarity Method:</label>
            <select
              value={rarityMethod}
              onChange={(e) => handleRarityMethodChange(e.target.value)}
              className='rarity-dropdown'
            >
              <option value='score'>Rarity Score</option>
              <option value='statistical'>Statistical Rarity</option>
              <option value='average'>Average Trait Rarity</option>
              <option value='jaccard'>Jaccard Distance</option>
              <option value='traitRating'>Trait Rarity Rating</option>
            </select>
          </div>

          <div className='existing-toggle'>
            <label>
              <input
                type='checkbox'
                checked={showOnlyExisting}
                onChange={(e) => handleShowOnlyExistingChange(e.target.checked)}
              />
              Show only existing NFTs
            </label>
          </div>

          <WalletSearchBox
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            wallet={wallet}
          />

          <div className='items-per-page-selector'>
            <label>Items per page:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className='items-per-page-dropdown'
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={500}>500</option>
            </select>
          </div>
        </div>

        <div className='collection-stats'>
          <span className='stat-item'>Total Items: {nftData.length.toLocaleString()}</span>
          <span className='stat-item'>Showing: {filteredData.length.toLocaleString()}</span>
          <span className='stat-item'>
            Current Method:{' '}
            {rarityMethod === 'statistical'
              ? 'Statistical Rarity'
              : rarityMethod === 'average'
              ? 'Average Trait Rarity'
              : rarityMethod === 'score'
              ? 'Rarity Score'
              : rarityMethod === 'jaccard'
              ? 'Jaccard Distance'
              : rarityMethod === 'traitRating'
              ? 'Trait Rarity Rating'
              : 'Statistical Rarity'}
          </span>
          {showOnlyExisting && (
            <span className='stat-item'>
              Existing NFTs:{' '}
              {nftData
                .filter((nft) => nft.NFTokenID && nft.NFTokenID.trim() !== '')
                .length.toLocaleString()}
            </span>
          )}
          <span className='stat-item'>
            Page {currentPage} of {totalPages} ({itemsPerPage} per page)
          </span>
        </div>
      </div>

      {loading ? (
        <div className='loading-container'>
          <div className='loading-spinner'></div>
          <p>Loading collection data...</p>
        </div>
      ) : (
        <>
          <div className='nft-grid'>
            {currentItems.map((nft, index) => (
              <NFTCard
                key={`${nft.name}-${index}`}
                nft={nft}
                rarityMethod={rarityMethod}
                collection='pixel'
              />
            ))}
          </div>

          {totalPages > 1 && (
            <div className='pagination'>
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className='pagination-btn'
              >
                Previous
              </button>

              <div className='page-numbers'>
                {[...Array(Math.min(5, totalPages))].map((_, index) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = index + 1;
                  } else {
                    const start = Math.max(1, currentPage - 2);
                    pageNum = start + index;
                    if (pageNum > totalPages) pageNum = totalPages - (4 - index);
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => goToPage(pageNum)}
                      className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className='pagination-btn'
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PixelCollection;
